/**
 * From the change log's summary of an entity (`GET /v1/cases/{id}/events/summary`) to the History
 * card's header and the change badge (PRD-0075 R-C1, R-C2).
 */
import type { ActivityMonth } from '@/components/entity-history/ActivitySparkline'
import type {
  EntityHistoryHeaderView,
  LastChangeView,
} from '@/components/entity-history/EntityHistoryHeader'
import {
  type ActivityMonthDto,
  type CaseEventDto,
  type EntitySummaryDto,
  isCaseEventKind,
} from '@/services/api/case-events'

/** A merge or property change younger than this marks the entity as changed (R-C2). */
export const CHANGE_BADGE_DAYS = 30
const DAY_MS = 86_400_000
/** The sparkline's span at most: five years of months. */
export const ACTIVITY_MAX_MONTHS = 60

/** Whether a change learned at `lastChangeAt` is younger than 30 days at `now`. */
export function isRecentChange(lastChangeAt: string | null, now: Date): boolean {
  if (!lastChangeAt) return false
  const at = Date.parse(lastChangeAt)
  return Number.isFinite(at) && now.getTime() - at < CHANGE_BADGE_DAYS * DAY_MS
}

const monthKey = (year: number, month: number) => `${year}-${String(month + 1).padStart(2, '0')}`

/**
 * Every month from the first one with a mention to `now` (UTC), the empty ones as 0; at most the
 * last `ACTIVITY_MAX_MONTHS`. A month later than `now` (a received date ahead of the clock) still shows.
 */
export function monthlyActivity(activity: readonly ActivityMonthDto[], now: Date): ActivityMonth[] {
  if (activity.length === 0) return []
  const counts = new Map(activity.map(m => [m.month, m.count]))
  const [firstYear, firstMonth] = activity[0].month.split('-').map(Number)
  const [lastYear, lastMonth] = activity[activity.length - 1].month.split('-').map(Number)
  const end = Math.max(now.getUTCFullYear() * 12 + now.getUTCMonth(), lastYear * 12 + lastMonth - 1)
  const start = Math.max(firstYear * 12 + firstMonth - 1, end - ACTIVITY_MAX_MONTHS + 1)
  const months: ActivityMonth[] = []
  for (let index = start; index <= end; index++) {
    const key = monthKey(Math.floor(index / 12), index % 12)
    months.push({ month: key, count: counts.get(key) ?? 0 })
  }
  return months
}

/** A logged property value as text; null when there was none. */
export function valueText(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map(item => valueText(item) ?? '').join(', ')
  return JSON.stringify(value)
}

function lastChangeOf(event: CaseEventDto | null): LastChangeView | null {
  if (!event) return null
  const kind = isCaseEventKind(event.kind) ? event.kind : null
  if (kind === 'property_changed') {
    const key = event.payload.key
    return {
      kind,
      key: typeof key === 'string' ? key : '',
      old: valueText(event.payload.old),
      new: valueText(event.payload.new),
      documentName: event.document_name,
      learnedAt: event.recorded_at,
      ...(event.payload.pinned === true ? { pinned: true } : {}),
    }
  }
  return { kind, learnedAt: event.recorded_at }
}

export function toHeaderView(summary: EntitySummaryDto, now: Date): EntityHistoryHeaderView {
  return {
    firstSeenAt: summary.first_seen_at,
    lastSeenAt: summary.last_seen_at,
    sourceCount: summary.source_count,
    activity: monthlyActivity(summary.activity, now),
    lastChange: lastChangeOf(summary.last_change),
    conflicts: summary.conflicts.map(conflict => ({
      key: conflict.key,
      value: valueText(conflict.value),
      previous: valueText(conflict.previous),
      documentName: conflict.document_name,
      previousDocumentName: conflict.previous_document_name,
      ...(conflict.pinned ? { pinned: true } : {}),
    })),
    changed: isRecentChange(summary.last_change_at, now),
  }
}
